import {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { format } from 'date-fns';
import { useDispatch, useSelector } from 'react-redux';
import { useFeatureIsOn, useFeatureValue } from '@growthbook/growthbook-react';
import { useAiChat } from '@/hooks/AiChat';
import { useAudioRecorder } from '@/hooks/useAudioRecorder';
import { useChatbot } from '@/contexts/Chatbot';
import { merlinActions } from '@/redux/modules';
import { WSS_ROUTES } from '@/components/Layout/Menu/HeaderV2/AiChat/AiChat.enum';
import { ATIVACAO_AICHAT_AUDIO_RECORDING, ATIVACAO_N8N_INHOUSE_URLS } from '@/config/GrowthBook/constants';
import { gatorAnalytics } from '@/analytics/gator';
import type { GatorChip, UseGatorChatResult } from './useGatorChat.types';

type MerlinState = {
  merlin?: {
    currentChat?: { threadid?: string };
    loadingTranscription?: boolean;
  };
};

const RECORDING_LIMIT_MS = 60000;

export function useGatorChat(): UseGatorChatResult {
  const dispatch = useDispatch();
  const chatbot = useChatbot() as {
    isActive: boolean;
    setRoute: (_route: string) => void;
  };

  const currentChat = useSelector((state: MerlinState) => state.merlin?.currentChat);
  const loadingTranscription = useSelector((state: MerlinState) => state.merlin?.loadingTranscription ?? false);

  const canShowAudio = useFeatureIsOn(ATIVACAO_AICHAT_AUDIO_RECORDING);
  const wssToggleRoutes = useFeatureValue(ATIVACAO_N8N_INHOUSE_URLS) as Record<string, string> ?? {};

  const { triggerExternalMessage, triggerExternalAudioBlob, handleOpenAlerts } = useAiChat() as {
    triggerExternalMessage: (_opts: { message: string }) => void;
    triggerExternalAudioBlob: (_blob: Blob) => void;
    handleOpenAlerts: () => void;
  };

  const chatIsOpen = Boolean(currentChat && currentChat.threadid);

  const handleAudioStop = useCallback((blob: Blob) => {
    gatorAnalytics.gatorHomeSendMessage({ messageType: 'VOICE' });
    triggerExternalAudioBlob(blob);
  }, [triggerExternalAudioBlob]);

  const { recording, handleStartRecording, handleStopRecording } = useAudioRecorder({ onStop: handleAudioStop });
  const isRecording = recording || loadingTranscription;

  const [timeStamp, setTimeStamp] = useState(0);
  const startTimeRef = useRef<number | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const handleStopRecordingRef = useRef(handleStopRecording);
  useEffect(() => {
    handleStopRecordingRef.current = handleStopRecording;
  });

  const formattedTime = useMemo(() => format(new Date(timeStamp), 'mm:ss'), [timeStamp]);

  useEffect(() => {
    if (recording) {
      startTimeRef.current = Date.now();
      setTimeStamp(0);
      intervalRef.current = setInterval(() => {
        const elapsed = Date.now() - (startTimeRef.current ?? Date.now());
        if (elapsed >= RECORDING_LIMIT_MS) {
          if (intervalRef.current) clearInterval(intervalRef.current);
          handleStopRecordingRef.current({ cancelRecording: false });
          return;
        }
        setTimeStamp(elapsed);
      }, 1000);
      return () => {
        if (intervalRef.current) clearInterval(intervalRef.current);
      };
    }
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    setTimeStamp(0);
    return undefined;
  }, [recording]);

  const handleSubmit = useCallback((message: string) => {
    gatorAnalytics.gatorHomeSendMessage({ messageType: 'TYPED' });
    triggerExternalMessage({ message });
  }, [triggerExternalMessage]);

  const handleChipClick = useCallback((chip: GatorChip, message?: string) => {
    gatorAnalytics.gatorShortcut({ label: chip.optionLabel });

    if (chatbot.isActive) {
      const iframeRoutes: Record<string, string> = {
        newSite: 'site',
        email: 'email',
        marketing: 'general',
        diagnostic: 'general',
        domainPurchase: 'domain',
        domainConfig: 'domain',
        checkAccount: 'general',
        contractProduct: 'general',
      };
      chatbot.setRoute(iframeRoutes[chip.action] || 'general');
    } else {
      const inhouseRoutes: Record<string, string> = {
        newSite: wssToggleRoutes.site || WSS_ROUTES.SITE,
        email: wssToggleRoutes.email || WSS_ROUTES.EMAIL,
        marketing: wssToggleRoutes.general || WSS_ROUTES.GENERAL,
        diagnostic: wssToggleRoutes.general || WSS_ROUTES.GENERAL,
        domainPurchase: wssToggleRoutes.domain || WSS_ROUTES.DOMAIN,
        domainConfig: wssToggleRoutes.domain || WSS_ROUTES.DOMAIN,
        checkAccount: wssToggleRoutes.general || WSS_ROUTES.GENERAL,
        contractProduct: wssToggleRoutes.general || WSS_ROUTES.GENERAL,
      };
      dispatch(merlinActions.inhouse.setRoute(inhouseRoutes[chip.action] || inhouseRoutes.diagnostic));
    }

    if (message) {
      triggerExternalMessage({ message });
    }
  }, [chatbot, dispatch, triggerExternalMessage, wssToggleRoutes]);

  const handleSuggestionPromptClick = useCallback((message: string) => {
    gatorAnalytics.gatorHomeSendMessage({ messageType: 'TYPED' });
    triggerExternalMessage({ message });
  }, [triggerExternalMessage]);

  return {
    handleSubmit,
    handleChipClick,
    handleSuggestionPromptClick,
    handleOpenAlerts,
    canShowAudio,
    recording,
    isRecording,
    loadingTranscription,
    handleStartRecording,
    handleStopRecording,
    formattedTime,
    chatIsOpen,
  };
}
